import { ThemeProvider } from '@emotion/react'
import { CssBaseline } from '@mui/material'
import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import './App.css'
import { AuthProvider } from './core/auth/AuthProvider'
import EmailAction from './core/auth/EmailAction'
import Layout from './core/layout/Layout'
import Onboard from './core/layout/Onboard'
import Public from './core/public/Auth'
import routes from './core/router/routes'
import portalTheme from './core/theme/portal'
import publicTheme from './core/theme/public'; // Import routes configuration

// Import Data Context Provider
import DataProvider from './core/data/DataProvider'
import CheckAuthState from './core/router/CheckAuthState'

// Redirect from the malformed /auth/action%20 path (trailing space from Firebase config) to the correct path
const ActionRedirect = () => {
  const { search } = useLocation()
  return <Navigate replace to={`/auth/action${search}`} />
}

// Create a client
const queryClient = new QueryClient()

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes)

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SentryRoutes>
          {/* Redirect malformed action URLs that have a URL-encoded space (%20) before the query string */}
          <Route
            element={<ActionRedirect />}
            path="/auth/action "
          />

          <Route
            element={
              <ThemeProvider theme={publicTheme}>
                <CssBaseline />
                <EmailAction />
              </ThemeProvider>
            }
            path={routes.authAction}
          />

          <Route
            element={
              <CheckAuthState>
                <ThemeProvider theme={publicTheme}>
                  <CssBaseline />
                  <Public />
                </ThemeProvider>
              </CheckAuthState>
            }
            path="/auth"
          />

          <Route
            element={
              <DataProvider>
                <CheckAuthState>
                  <ThemeProvider theme={portalTheme}>
                    <CssBaseline />
                    <Onboard />
                  </ThemeProvider>
                </CheckAuthState>
              </DataProvider>
            }
            path={routes.onboard}
          />

          <Route
            element={
              <DataProvider>
                <CheckAuthState>
                  <ThemeProvider theme={portalTheme}>
                    <CssBaseline />
                    <Layout />
                  </ThemeProvider>
                </CheckAuthState>
              </DataProvider>
            }
            path={routes.root}
          />
        </SentryRoutes>
      </AuthProvider>

      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}

export default App
